import { css } from "styled-system/css";

/** Estilos de marca SFTURNOS (indigo / slate). */
export const cf = {
  ink: "#0f172a",
  muted: "#64748b",
  border: "#e2e8f0",
  inputBorder: "#dbe2ea",
  pageGray: "#f4f6f8",
  heroDark: "#0b1220",

  pageBg: css({
    minH: "100vh",
    bg: "linear-gradient(180deg, #f8fafc, #eef2f7)",
    color: "#0f172a",
  }),

  /** Panel oscuro con grilla (sidebar / hero). */
  darkPanel: css({
    bg: "#0b1220",
    color: "white",
    position: "relative",
    overflow: "hidden",
    _before: {
      content: '""',
      position: "absolute",
      inset: 0,
      background:
        "linear-gradient(rgba(99, 102, 241, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(99, 102, 241, 0.08) 1px, transparent 1px)",
      backgroundSize: "28px 28px",
      opacity: 0.8,
      pointerEvents: "none",
    },
    _after: {
      content: '""',
      position: "absolute",
      inset: 0,
      background: "radial-gradient(700px 380px at 15% 8%, rgba(99, 102, 241, 0.28), transparent 58%)",
      pointerEvents: "none",
    },
  }),

  darkPanelInner: css({
    position: "relative",
    zIndex: 1,
  }),

  labelCaps: css({
    fontSize: "xs",
    letterSpacing: "widest",
    color: "#64748b",
    mb: "2",
  }),

  titleHero: css({
    fontSize: { base: "4xl", md: "5xl" },
    fontWeight: "bold",
    letterSpacing: "tight",
    lineHeight: "1.05",
    color: "#0f172a",
  }),

  titleHeroOnDark: css({
    fontSize: { base: "3xl", md: "4xl" },
    fontWeight: "bold",
    letterSpacing: "tight",
    lineHeight: "1.05",
    color: "white",
  }),

  subtitle: css({
    fontSize: "md",
    color: "#64748b",
    mt: "2",
    lineHeight: "1.5",
  }),

  subtitleOnDark: css({
    maxW: "34ch",
    color: "rgba(255,255,255,0.78)",
    fontSize: "sm",
    lineHeight: "1.6",
  }),

  card: css({
    bg: "white",
    rounded: "xl",
    borderWidth: "1px",
    borderColor: "#e2e8f0",
    boxShadow: "0 24px 48px rgba(15, 23, 42, 0.10)",
  }),

  cardPadding: css({
    p: { base: "6", md: "7" },
  }),

  input: css({
    h: "12",
    px: "4",
    fontSize: "md",
    color: "#0f172a",
    borderWidth: "1px",
    borderColor: "#dbe2ea",
    rounded: "lg",
    bg: "#f8fafc",
    width: "100%",
    _placeholder: { color: "#94a3b8" },
    _focus: {
      outline: "none",
      bg: "white",
      borderColor: "#334155",
      boxShadow: "0 0 0 3px rgba(51, 65, 85, 0.12)",
    },
  }),

  tag: css({
    fontSize: "xs",
    px: "3",
    py: "1.5",
    rounded: "md",
    borderWidth: "1px",
    borderColor: "rgba(255,255,255,0.25)",
    color: "rgba(255,255,255,0.82)",
    bg: "rgba(255,255,255,0.04)",
  }),

  mainArea: css({
    flex: "1",
    minW: 0,
    p: { base: "4", md: "8" },
    bg: "linear-gradient(180deg, #f8fafc, #eef2f7)",
  }),

  contentMax: css({
    maxW: "1200px",
    mx: "auto",
  }),

  /** Enlaces con apariencia de botón sobre fondo oscuro (usar en `<Link>`, nunca anidar `<button>`). */
  linkPrimaryOnDark: css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    px: "4",
    py: "2.5",
    rounded: "lg",
    fontSize: "sm",
    fontWeight: "semibold",
    color: "white",
    bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
    textDecoration: "none",
    borderWidth: "0",
    cursor: "pointer",
    transition: "opacity 0.15s, transform 0.15s",
    _hover: { opacity: 0.94 },
    _active: { transform: "translateY(1px)" },
  }),

  linkOutlineOnDark: css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    px: "4",
    py: "2.5",
    rounded: "lg",
    fontSize: "sm",
    fontWeight: "semibold",
    color: "white",
    borderWidth: "1px",
    borderColor: "rgba(255,255,255,0.32)",
    bg: "rgba(255,255,255,0.06)",
    textDecoration: "none",
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
    _hover: {
      bg: "rgba(255,255,255,0.12)",
      borderColor: "rgba(255,255,255,0.45)",
    },
  }),

  linkNavOnDark: css({
    fontSize: "sm",
    fontWeight: "500",
    color: "rgba(255,255,255,0.78)",
    textDecoration: "none",
    px: "2",
    py: "1.5",
    rounded: "md",
    transition: "color 0.15s, background 0.15s",
    _hover: { color: "white", bg: "rgba(255,255,255,0.06)" },
  }),

  landingHeaderRow: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "4",
    pb: { base: "8", md: "10" },
    mb: { base: "8", md: "10" },
    borderBottomWidth: "1px",
    borderColor: "rgba(255,255,255,0.08)",
  }),

  landingLogo: css({
    fontWeight: "bold",
    fontSize: "lg",
    letterSpacing: "tight",
    color: "white",
    textDecoration: "none",
  }),

  landingNavActions: css({
    display: "flex",
    alignItems: "center",
    gap: { base: "2", sm: "3" },
    flexWrap: "wrap",
    justifyContent: "flex-end",
  }),

  /** Tarjeta en el área principal del panel (fondo claro; el sidebar sigue en `darkPanel`). */
  dashSurface: css({
    bg: "white",
    borderWidth: "1px",
    borderColor: "#e2e8f0",
    rounded: "xl",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
  }),

  dashSurfaceStrong: css({
    bg: "white",
    borderWidth: "1px",
    borderColor: "#e2e8f0",
    rounded: "xl",
    boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08)",
  }),

  dashText: css({
    color: "#0f172a",
  }),

  dashMuted: css({
    color: "#64748b",
  }),

  dashEyebrow: css({
    fontSize: "xs",
    letterSpacing: "widest",
    color: "#64748b",
  }),

  /**
   * Wrapper externo para contenedores con scroll horizontal en mobile.
   * Muestra degradados en los bordes izquierdo y derecho para indicar overflow.
   * Usar junto con scrollFadeXInner. Configurar el color del fondo con
   * la variable CSS `--scroll-fade-bg` (default: white).
   *
   * Uso:
   *   <div className={cf.scrollFadeXOuter} style={{ "--scroll-fade-bg": "#f8fafc" } as React.CSSProperties}>
   *     <div className={cf.scrollFadeXInner}>
   *       <div style={{ minWidth: "680px" }}>{content}</div>
   *     </div>
   *   </div>
   */
  scrollFadeXOuter: css({
    position: "relative",
    _before: {
      content: '""',
      display: { base: "block", md: "none" },
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      w: "6",
      background: "linear-gradient(to right, var(--scroll-fade-bg, white), transparent)",
      zIndex: 2,
      pointerEvents: "none",
    },
    _after: {
      content: '""',
      display: { base: "block", md: "none" },
      position: "absolute",
      top: 0,
      bottom: 0,
      right: 0,
      w: "6",
      background: "linear-gradient(to left, var(--scroll-fade-bg, white), transparent)",
      zIndex: 2,
      pointerEvents: "none",
    },
  }),

  /** Elemento interno scrollable — usar dentro de scrollFadeXOuter. */
  scrollFadeXInner: css({
    overflowX: { base: "auto", md: "visible" },
    WebkitOverflowScrolling: "touch",
  }),
};
